import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  addItem,
  getSection,
  listItems,
  moveItem,
  parseDream,
  readAbout,
  readLabel,
  removeItem,
  sectionHints,
  setAboutText,
  setForWhom,
  setItemDone,
  setItemText,
  setLabel,
  updateSection,
  type ItemKind,
} from "@buildlab/codeview";
import type { FileMap } from "@buildlab/shared";
import { TestLab } from "./TestLab";
import "./ProjectNotes.css";

// The Notes view of PROJECT.md (decision 59): one card per section, so students never have to face
// raw Markdown. Each card rewrites only its own section (helpers in packages/codeview/src/notes.ts),
// and saving goes through the workspace's normal file save, so the helper reads the same file.
// "Edit as text" is always one click away.

interface Props {
  /** The whole project; the notes are its PROJECT.md, and "Check my tests" plays the rest. */
  files: FileMap;
  /** True while the helper is editing: the notes follow along but can't be changed. */
  readOnly: boolean;
  onChange: (md: string) => void;
  onEditAsText: () => void;
  projectId: string;
  /** Saves pending edits so "Check my tests" sees the latest tests. */
  beforeCheck: () => Promise<void>;
  onFiles: (files: FileMap) => void;
  onBugReport: (text: string) => void;
}

export function ProjectNotes({ files, readOnly, onChange, onEditAsText, projectId, beforeCheck, onFiles, onBugReport }: Props) {
  const [testing, setTesting] = useState(false);
  const md = files["PROJECT.md"] ?? "";
  const about = readAbout(md);
  const plan = getSection(md, "Plan") ?? "";
  const tests = getSection(md, "Tests") ?? "";
  const dream = getSection(md, "Dream") !== null ? parseDream(md) : null;
  const section = (name: string) => (change: (text: string) => string) => onChange(updateSection(md, name, change));

  return (
    <div className="pnotes" aria-label="Project notes">
      <div className="notes-head">
        <div>
          <span className="eyebrow">Project notes · PROJECT.md</span>
          <p>Your helper reads these notes before every change.</p>
        </div>
        <button type="button" className="outline small" onClick={onEditAsText}>
          Edit as text
        </button>
      </div>
      {readOnly && <p className="notes-busy">The helper is working. Your notes update as it goes, and you can edit them when it's done.</p>}

      <NoteCard name="about" title="What are you making?" why="A sentence or two about your project, and who it's for." hints={about.hints}>
        <Draft
          multiline
          label="What it is"
          value={about.description}
          placeholder="A game where you dodge falling blocks."
          disabled={readOnly}
          onCommit={(v) => onChange(setAboutText(md, v))}
        />
        <Draft
          label="Who is it for?"
          value={about.forWhom}
          placeholder={about.forHint && !/^who is (this|it) for\??$/i.test(about.forHint) ? `${about.forHint[0]!.toUpperCase()}${about.forHint.slice(1)}` : "Like: my little brother, who likes quick games"}
          disabled={readOnly}
          onCommit={(v) => onChange(setForWhom(md, v))}
        />
      </NoteCard>

      <NoteCard name="plan" title="The plan" why="Your must-haves, in order. Keep each step small enough for one message to your helper." hints={sectionHints(plan)}>
        <ItemList
          kind="check"
          text={plan}
          noun="step"
          addLabel="Add a step"
          example="Player moves left and right"
          disabled={readOnly}
          reorder
          onChange={section("Plan")}
        />
        <Draft
          label="Later (extras for after the must-haves work)"
          value={readLabel(plan, "Later") ?? ""}
          placeholder="Like: high score, sound effects"
          disabled={readOnly}
          onCommit={(v) => section("Plan")((t) => setLabel(t, "Later", v))}
        />
      </NoteCard>

      <NoteCard
        name="screens"
        title="Screens"
        why="What someone sees, one screen or page at a time. Tick a screen once it's built."
        hints={sectionHints(getSection(md, "Screens") ?? "")}
      >
        <ItemList
          kind="bullet"
          text={getSection(md, "Screens") ?? ""}
          noun="screen"
          addLabel="Add a screen"
          example="Game over: your score and “press R to restart”"
          disabled={readOnly}
          reorder
          onChange={section("Screens")}
        />
      </NoteCard>

      <NoteCard name="rules" title="Rules for your helper" why="Your helper follows these every time it changes your project." hints={sectionHints(getSection(md, "Rules") ?? "")}>
        <RuleChips text={getSection(md, "Rules") ?? ""} disabled={readOnly} onChange={section("Rules")} />
      </NoteCard>

      <NoteCard name="tests" title="Tests" why="How you'll know it works: what someone does, and what should happen." hints={sectionHints(tests)}>
        <ItemList
          kind="check"
          text={tests}
          noun="test"
          addLabel="Add a test"
          example="Clicking the mole adds 1 point"
          disabled={readOnly}
          onChange={section("Tests")}
        />
        {listItems(tests, "check").length > 0 && (
          <div>
            <button
              type="button"
              className="primary small"
              disabled={readOnly}
              onClick={async () => {
                await beforeCheck();
                setTesting(true);
              }}
            >
              Check my tests
            </button>
          </div>
        )}
      </NoteCard>

      {dream && (
        <NoteCard name="dream" title="The Impossible List" why="Your no-limits wishes, sorted by the helper. Sort them from the Project map.">
          <dl className="note-dream">
            {(
              [
                ["Today", dream.today],
                ["This week", dream.week],
                ["Someday", dream.someday],
                ["Needs a grown-up's help", dream.grownup],
                ["Not sorted yet", dream.unsorted.map((text) => ({ text, done: false }))],
              ] as const
            )
              .filter(([, items]) => items.length)
              .map(([label, items]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  {items.map((i, n) => (
                    <dd key={n} className={i.done ? "is-done" : ""}>
                      {i.text}
                    </dd>
                  ))}
                </div>
              ))}
          </dl>
        </NoteCard>
      )}

      <NoteCard name="log" title="Log" why="Notes for future you: what you tried, what a tester said, what's next." hints={sectionHints(getSection(md, "Log") ?? "")}>
        <ItemList
          kind="line"
          text={getSection(md, "Log") ?? ""}
          noun="note"
          addLabel="Add a note"
          example="My brother couldn't find the start button, so I made it bigger"
          disabled={readOnly}
          onChange={section("Log")}
        />
      </NoteCard>

      {testing && (
        <TestLab
          projectId={projectId}
          files={files}
          onClose={() => setTesting(false)}
          onFiles={onFiles}
          onBugReport={(text) => {
            setTesting(false);
            onBugReport(text);
          }}
        />
      )}
    </div>
  );
}

/** Shown above the text editor when PROJECT.md is open as text, to get back to the cards. */
export function NotesTextBar({ onBack }: { onBack: () => void }) {
  return (
    <div className="notes-textbar">
      <span className="small">Editing PROJECT.md as text</span>
      <button type="button" className="linklike small" onClick={onBack}>
        Back to notes
      </button>
    </div>
  );
}

function NoteCard({ name, title, why, hints = [], children }: { name: string; title: string; why: string; hints?: string[]; children: ReactNode }) {
  const id = useId();
  return (
    <section className={`note-card nc-${name}`} aria-labelledby={id}>
      <h3 id={id}>{title}</h3>
      <p className="note-why">{why}</p>
      {hints.map((h, i) => (
        <p key={i} className="note-hint">
          Tip: {h}
        </p>
      ))}
      {children}
    </section>
  );
}

/**
 * A text field that edits a copy and saves on Enter or when you leave it, so half-typed text never
 * reshapes the Markdown. It picks up outside changes (like the helper's) while you're not typing in it.
 */
function Draft({
  value,
  onCommit,
  label,
  placeholder,
  disabled,
  multiline,
  className,
  hideLabel,
}: {
  value: string;
  onCommit: (v: string) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  className?: string;
  hideLabel?: boolean;
}) {
  const id = useId();
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);
  const commit = () => {
    if (draft.trim() !== value.trim()) onCommit(draft);
  };
  const common = {
    id,
    value: draft,
    placeholder,
    disabled,
    className: `note-field${className ? ` ${className}` : ""}`,
    onFocus: () => (focused.current = true),
    onBlur: () => {
      focused.current = false;
      commit();
    },
  };
  return (
    <div className="note-draft">
      <label htmlFor={id} className={hideLabel ? "sr-only" : "note-label"}>
        {label}
      </label>
      {multiline ? (
        <textarea {...common} rows={2} onChange={(e) => setDraft(e.target.value)} />
      ) : className === "inline" ? (
        // A list item: one line of Markdown, but it wraps on screen so long steps stay readable.
        <textarea
          {...common}
          rows={1}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value.replace(/[\r\n]+/g, " "))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") setDraft(value);
          }}
        />
      ) : (
        <input
          {...common}
          type="text"
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setDraft(value);
          }}
        />
      )}
    </div>
  );
}

/** A small "add one" form under a list. */
function AddForm({ label, example, disabled, onAdd }: { label: string; example: string; disabled: boolean; onAdd: (text: string) => void }) {
  const [text, setText] = useState("");
  const id = useId();
  return (
    <form
      className="note-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (!text.trim()) return;
        onAdd(text);
        setText("");
      }}
    >
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input id={id} type="text" maxLength={200} value={text} disabled={disabled} placeholder={`${label}, like: ${example}`} onChange={(e) => setText(e.target.value)} />
      <button type="submit" className="outline small" disabled={disabled || !text.trim()}>
        Add
      </button>
    </form>
  );
}

function ItemList({
  kind,
  text,
  noun,
  addLabel,
  example,
  disabled,
  reorder,
  onChange,
}: {
  kind: ItemKind;
  text: string;
  noun: string;
  addLabel: string;
  example: string;
  disabled: boolean;
  reorder?: boolean;
  onChange: (change: (text: string) => string) => void;
}) {
  const items = listItems(text, kind);
  return (
    <>
      {items.length > 0 && (
        <ol className={`note-items k-${kind}`}>
          {items.map((item, i) => (
            <li key={`${i}:${item.text}`} className={`${item.done ? "is-done" : ""}${/^\(.*\)$/.test(item.text) ? " is-placeholder" : ""}`}>
              {kind !== "line" && (
                <input
                  type="checkbox"
                  checked={item.done}
                  disabled={disabled}
                  aria-label={`Done: ${item.text}`}
                  onChange={(e) => onChange((t) => setItemDone(t, kind, i, e.target.checked))}
                />
              )}
              <Draft
                hideLabel
                label={`${noun[0]!.toUpperCase()}${noun.slice(1)} ${i + 1}`}
                value={item.text}
                disabled={disabled}
                className="inline"
                onCommit={(v) => onChange((t) => setItemText(t, kind, i, v))}
              />
              {reorder && (
                <>
                  <button type="button" className="note-icon" disabled={disabled || i === 0} aria-label={`Move ${noun} ${i + 1} up`} onClick={() => onChange((t) => moveItem(t, kind, i, -1))}>
                    ↑
                  </button>
                  <button
                    type="button"
                    className="note-icon"
                    disabled={disabled || i === items.length - 1}
                    aria-label={`Move ${noun} ${i + 1} down`}
                    onClick={() => onChange((t) => moveItem(t, kind, i, 1))}
                  >
                    ↓
                  </button>
                </>
              )}
              <button type="button" className="note-icon remove" disabled={disabled} aria-label={`Remove ${noun} ${i + 1}`} onClick={() => onChange((t) => removeItem(t, kind, i))}>
                ×
              </button>
            </li>
          ))}
        </ol>
      )}
      {items.length === 0 && <p className="note-empty">No {noun}s yet.</p>}
      <AddForm label={addLabel} example={example} disabled={disabled} onAdd={(v) => onChange((t) => addItem(t, kind, v))} />
    </>
  );
}

function RuleChips({ text, disabled, onChange }: { text: string; disabled: boolean; onChange: (change: (text: string) => string) => void }) {
  const rules = listItems(text, "line");
  return (
    <>
      {rules.length > 0 ? (
        <ul className="note-chips">
          {rules.map((r, i) => (
            <li key={`${i}:${r.text}`}>
              <span>{r.text}</span>
              <button type="button" className="note-icon remove" disabled={disabled} aria-label={`Remove rule: ${r.text}`} onClick={() => onChange((t) => removeItem(t, "line", i))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="note-empty">No rules yet.</p>
      )}
      <AddForm label="Add a rule" example="All buttons are round and teal" disabled={disabled} onAdd={(v) => onChange((t) => addItem(t, "line", v))} />
    </>
  );
}
